/* ETA estimate for a picked-up PU, based on straight-line distance
   from the origin warehouse to the client's delivery address plus a
   simplified US FMCSA Hours-of-Service (HOS) drive-time model.
   2026-09-12 — Arturo: once he records the actual pickup date/time
   from the driver's BOL (see InvoiceRegistry.js's new pickupDate/
   pickupTime fields), this estimates when the load should reach the
   client, shown on the Active Shipments dashboard card.

   "Calc file, no UI" convention (fobPricesForecast.js, saleStatusCalc.js):
   pure functions, `var`/function-declaration style, no direct
   Supabase reads inside the math itself — geocoding is the one
   exception, since it's the only way to turn a street address into a
   distance, and its result is cached so it only ever runs once per
   unique address (see geocodeAddress below).

   Approximations, documented rather than hidden:
   - Distance is straight-line (haversine) × ROAD_FACTOR, not a real
     routed road distance — no paid routing API (Arturo chose the
     free/approximate option over Google Maps/Mapbox).
   - Average speed is a flat assumption (AVG_SPEED_MPH), not real
     traffic/weather conditions.
   - HOS model: 11 hours driving per duty day (49 CFR 395.3), then a
     mandatory 10-hour rest before the next duty day's driving can
     start. The 14-hour on-duty window, 30-minute break after 8 hours,
     and 60/70-hour-in-7/8-days limits are NOT modeled — folded into
     the conservative AVG_SPEED_MPH/ROAD_FACTOR margin instead. Good
     enough for a dashboard ETA hint, not a dispatch-grade planner. */

var ROAD_FACTOR = 1.25; /* straight-line -> approximate road distance */
var AVG_SPEED_MPH = 50; /* legal cruising speed minus stops/fueling/inspections */
var MAX_DRIVE_HOURS_PER_DAY = 11; /* 49 CFR 395.3(a)(1) */
var REQUIRED_REST_HOURS = 10; /* 49 CFR 395.3(a)(1) */

function toRad(deg) { return (deg * Math.PI) / 180; }

/* Great-circle distance in miles between two lat/lng points. */
export function haversineMiles(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
  var R = 3958.8; /* Earth radius, miles */
  var dLat = toRad(lat2 - lat1);
  var dLng = toRad(lng2 - lng1);
  var a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/* Simulates HOS-limited driving from a pickup Date to estimate
   arrival. roadMiles should already include ROAD_FACTOR (see
   estimateEta below, which is the intended entry point — this is
   exported separately so it's independently testable). */
export function simulateHosArrival(pickupDate, roadMiles) {
  if (!(pickupDate instanceof Date) || isNaN(pickupDate.getTime())) return null;
  if (roadMiles == null || roadMiles <= 0) return new Date(pickupDate.getTime());
  var remainingHours = roadMiles / AVG_SPEED_MPH;
  var current = new Date(pickupDate.getTime());
  var driveHoursNeededSafety = 0; /* guards against pathological input */
  while (remainingHours > 0 && driveHoursNeededSafety < 1000) {
    var driveToday = Math.min(remainingHours, MAX_DRIVE_HOURS_PER_DAY);
    current = new Date(current.getTime() + driveToday * 3600000);
    remainingHours -= driveToday;
    driveHoursNeededSafety += 1;
    if (remainingHours > 0.01) {
      current = new Date(current.getTime() + REQUIRED_REST_HOURS * 3600000);
    }
  }
  return current;
}

/* 2026-09-14 — Arturo: the Four Seasons truck route isn't a direct
   run — it makes stops along the way for other deliveries before
   reaching FS, so the straight HOS drive-time estimate below always
   ran early. Flat padding added AFTER the HOS simulation (not extra
   miles — the stops don't add distance, they add dwell time at each
   stop), so it doesn't interact with the multi-day rest-break logic
   above. Caller passes this only for Four Seasons destinations. */
var MULTI_STOP_EXTRA_HOURS = 8;

/* Full estimate: origin/destination lat-lng + pickup timestamp -> ETA
   Date, plus the inputs used (so callers can show "approx" context).
   Returns null if either coordinate is missing (caller should just
   omit the ETA line rather than guess). options.multiStop adds the
   flat Four Seasons multi-stop padding above. */
export function estimateEta(pickupDate, originLatLng, destLatLng, options) {
  if (!originLatLng || !destLatLng) return null;
  var straightMiles = haversineMiles(originLatLng.lat, originLatLng.lng, destLatLng.lat, destLatLng.lng);
  if (straightMiles == null) return null;
  var roadMiles = straightMiles * ROAD_FACTOR;
  var eta = simulateHosArrival(pickupDate, roadMiles);
  if (!eta) return null;
  if (options && options.multiStop) eta = new Date(eta.getTime() + MULTI_STOP_EXTRA_HOURS * 3600000);
  return { eta: eta, straightMiles: straightMiles, roadMiles: roadMiles };
}

/* ─── Geocoding, cached in localStorage (2026-09-12) ────────────────
   Same shape of problem as the rest of this app's "online-first"
   reads: geocoding a street address is a network call, but a given
   client/warehouse's address barely ever changes, so there's no
   reason to re-geocode it on every dashboard render. Cache key is the
   exact address string (case-insensitive) — a changed address is a
   different string, so it naturally re-geocodes instead of serving a
   stale result. */
var GEOCODE_CACHE_KEY = "gfi-geocode-cache";

function loadGeocodeCache() {
  try {
    var raw = localStorage.getItem(GEOCODE_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) { return {}; }
}
function saveGeocodeCache(cache) {
  try { localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache)); } catch (e) { /* quota — non-fatal, just re-geocodes next time */ }
}

export function buildAddressString(entity) {
  if (!entity) return "";
  var parts = [entity.address, entity.city, entity.state, entity.zip].map(function (s) { return (s || "").trim(); }).filter(Boolean);
  return parts.join(", ");
}

/* Resolves lat/lng for a free-form US address, using the cache first
   and falling back to the geocode Netlify Function (US Census
   Bureau's free public geocoder — no API key, no cost). Returns null
   (never throws) if the address is empty, the function fails, or no
   match is found, so callers can just skip showing an ETA rather than
   crash a dashboard render over a network hiccup. */
export async function geocodeAddress(addressString) {
  var key = (addressString || "").trim().toLowerCase();
  if (!key) return null;
  var cache = loadGeocodeCache();
  if (cache[key]) return cache[key];
  try {
    var res = await fetch("/.netlify/functions/geocode?address=" + encodeURIComponent(addressString));
    var data = await res.json();
    if (!data || !data.success || data.lat == null || data.lng == null) {
      cache[key] = null;
      saveGeocodeCache(cache);
      return null;
    }
    var coords = { lat: data.lat, lng: data.lng };
    cache[key] = coords;
    saveGeocodeCache(cache);
    return coords;
  } catch (e) {
    return null;
  }
}
